import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Building2, Loader2, Pencil, Plus, X } from 'lucide-react';
import api from '@/lib/api';
import { useConfirm } from '@/hooks/useConfirm';

interface Tenant {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
}

const TenantsAdminPage: React.FC = () => {
  const { t } = useTranslation('common');
  const tt = (key: string, fallback: string) => t(key, { defaultValue: fallback });
  const { confirm } = useConfirm();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Tenant | null>(null);
  const [saving, setSaving] = useState(false);
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api
      .get('/tenants')
      .then((r) => setTenants(r.data?.tenants ?? []))
      .catch(() =>
        toast.error(
          tt('tenantsAdmin.toasts.loadError', 'Não foi possível carregar as tenants'),
        ),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setSlug('');
    setName('');
    setShowForm(true);
  };

  const openEdit = (tenant: Tenant) => {
    setEditing(tenant);
    setSlug(tenant.id);
    setName(tenant.name);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    try {
      setSaving(true);
      if (editing) {
        await api.patch(`/tenants/${editing.id}`, { name: name.trim() });
        toast.success(tt('tenantsAdmin.toasts.updated', 'Tenant atualizada'));
      } else {
        await api.post('/tenants', {
          id: slug.trim().toLowerCase(),
          name: name.trim(),
        });
        toast.success(tt('tenantsAdmin.toasts.created', 'Tenant criada'));
      }
      setShowForm(false);
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: unknown } } })?.response
        ?.data?.message;
      toast.error(
        typeof msg === 'string'
          ? msg
          : tt('tenantsAdmin.toasts.saveError', 'Erro ao salvar tenant'),
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (tenant: Tenant) => {
    const confirmed = await confirm({
      title: tenant.isActive
        ? tt('tenantsAdmin.confirm.deactivateTitle', 'Desativar tenant')
        : tt('tenantsAdmin.confirm.activateTitle', 'Ativar tenant'),
      message: tenant.isActive
        ? `Desativar "${tenant.name}"? Usuários não poderão ser atribuídos a ela.`
        : `Ativar "${tenant.name}"?`,
      confirmText: tenant.isActive
        ? tt('tenantsAdmin.confirm.deactivate', 'Desativar')
        : tt('tenantsAdmin.confirm.activate', 'Ativar'),
    });
    if (!confirmed) return;
    try {
      await api.patch(`/tenants/${tenant.id}`, { isActive: !tenant.isActive });
      load();
    } catch {
      toast.error(
        tt('tenantsAdmin.toasts.toggleError', 'Erro ao atualizar tenant'),
      );
    }
  };

  const formatDate = (value: string): string =>
    new Date(value).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {tt('tenantsAdmin.title', 'Tenants')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {tt(
              'tenantsAdmin.subtitle',
              'Espaços de isolamento de formulários e usuários',
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          <Plus className="h-4 w-4" />
          {tt('tenantsAdmin.newTenant', 'Nova tenant')}
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            {tt('common.loading', 'Carregando...')}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800">
                <th className="px-4 py-3">{tt('tenantsAdmin.table.name', 'Nome')}</th>
                <th className="px-4 py-3">{tt('tenantsAdmin.table.slug', 'Slug')}</th>
                <th className="px-4 py-3">{tt('tenantsAdmin.table.status', 'Status')}</th>
                <th className="px-4 py-3">{tt('tenantsAdmin.table.created', 'Criada em')}</th>
                <th className="px-4 py-3 text-right">{tt('common.actions', 'Ações')}</th>
              </tr>
            </thead>
            <tbody>
              {tenants.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                    <Building2 className="mx-auto mb-2 h-6 w-6 opacity-50" />
                    {tt('tenantsAdmin.empty', 'Nenhuma tenant cadastrada')}
                  </td>
                </tr>
              ) : (
                tenants.map((tenant) => (
                  <tr
                    key={tenant.id}
                    className="border-b border-slate-100 last:border-0 dark:border-slate-800"
                  >
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                      {tenant.name}
                    </td>
                    <td className="px-4 py-3">
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {tenant.id}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          tenant.isActive
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                        }`}
                      >
                        {tenant.isActive
                          ? tt('common.active', 'Ativa')
                          : tt('common.inactive', 'Inativa')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {formatDate(tenant.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(tenant)}
                          title={tt('common.edit', 'Editar')}
                          className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleActive(tenant)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                          {tenant.isActive
                            ? tt('common.deactivate', 'Desativar')
                            : tt('common.activate', 'Ativar')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {editing
                  ? tt('tenantsAdmin.form.editTitle', 'Editar tenant')
                  : tt('tenantsAdmin.form.createTitle', 'Nova tenant')}
              </h2>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  {tt('tenantsAdmin.form.slug', 'Slug (identificador)')}
                </label>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  disabled={Boolean(editing)}
                  placeholder="ex.: acme"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:disabled:bg-slate-800/50"
                  required
                  minLength={3}
                  maxLength={63}
                />
                <p className="mt-1 text-xs text-slate-400">
                  Letras minúsculas, números e hífens. Não pode ser alterado depois.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  {tt('tenantsAdmin.form.name', 'Nome')}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800"
                  required
                  minLength={2}
                  maxLength={120}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  {tt('common.cancel', 'Cancelar')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editing
                    ? tt('common.save', 'Salvar')
                    : tt('common.create', 'Criar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantsAdminPage;
